// src/modules/analytics/hooks/use-users-stats.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  LlmUsageStatByUserItem,
  UserStatsSortColumn,
  SortDirection,
  AnalyticsFilters,
} from '../types';
import { getStatsByUser } from '../api';

const DEFAULT_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_SORT_COLUMN: UserStatsSortColumn = 'tokens_total';
const DEFAULT_SORT_DIRECTION: SortDirection = 'DESC';

// Применённые фильтры страницы, влияющие на таблицу пользователей
export type UsersStatsPageFilters = Pick<AnalyticsFilters, 'date_from' | 'date_to' | 'project_id'>;

export interface UseUsersStatsResult {
  items: LlmUsageStatByUserItem[];
  count: number;
  loading: boolean;
  error: string | null;
  query: string;
  setQuery: (q: string) => void;
  sortColumn: UserStatsSortColumn;
  sortDirection: SortDirection;
  toggleSort: (column: UserStatsSortColumn) => void;
  page: number;
  limit: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
}

export function useUsersStats(pageFilters: UsersStatsPageFilters): UseUsersStatsResult {
  const [items, setItems] = useState<LlmUsageStatByUserItem[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQueryState] = useState('');
  const [sort, setSort] = useState<{ column: UserStatsSortColumn; direction: SortDirection }>({
    column: DEFAULT_SORT_COLUMN,
    direction: DEFAULT_SORT_DIRECTION,
  });
  const { column: sortColumn, direction: sortDirection } = sort;
  const [limit, setLimitState] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);

  // reqId защищает от гонок: устаревшие ответы отбрасываются
  const reqIdRef = useRef(0);

  const load = useCallback(
    async (params: {
      query: string;
      sortColumn: UserStatsSortColumn;
      sortDirection: SortDirection;
      limit: number;
      offset: number;
      filters: UsersStatsPageFilters;
    }) => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const data = await getStatsByUser({
          date_from: params.filters.date_from,
          date_to: params.filters.date_to,
          project_id: params.filters.project_id,
          query: params.query.trim() || undefined,
          sort_column: params.sortColumn,
          sort_direction: params.sortDirection,
          limit: params.limit,
          offset: params.offset,
        });
        if (reqId !== reqIdRef.current) return;
        setItems(data.items);
        setCount(data.count);
      } catch (e) {
        if (reqId !== reqIdRef.current) return;
        setError(e instanceof Error ? e.message : 'Ошибка загрузки статистики пользователей');
        setItems([]);
        setCount(0);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    },
    []
  );

  // Debounce только для query; остальные изменения грузим сразу
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevQueryRef = useRef(query);

  // Фильтры страницы сравниваем по значениям, а не по identity объекта:
  // use-analytics создаёт новый объект при каждом «Применить»
  const { date_from: dateFrom, date_to: dateTo, project_id: projectId } = pageFilters;
  const filtersKey = `${dateFrom}|${dateTo}|${projectId ?? ''}`;

  // Сброс offset при изменении фильтров страницы (новый период/проект) —
  // до загрузки, чтобы не было лишнего запроса со старым offset
  const prevFiltersKeyRef = useRef(filtersKey);

  useEffect(() => {
    const filtersChanged = prevFiltersKeyRef.current !== filtersKey;
    prevFiltersKeyRef.current = filtersKey;
    if (filtersChanged && offset !== 0) {
      setOffset(0); // эффект перезапустится с offset=0
      return undefined;
    }

    const params = {
      query,
      sortColumn,
      sortDirection,
      limit,
      offset,
      filters: { date_from: dateFrom, date_to: dateTo, project_id: projectId },
    };
    const queryChanged = prevQueryRef.current !== query;
    prevQueryRef.current = query;

    if (queryChanged) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        load(params);
      }, SEARCH_DEBOUNCE_MS);
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }

    load(params);
    return undefined;
  }, [
    query,
    sortColumn,
    sortDirection,
    limit,
    offset,
    filtersKey,
    dateFrom,
    dateTo,
    projectId,
    load,
  ]);

  const setQuery = useCallback((q: string) => {
    setQueryState(q);
    setOffset(0);
  }, []);

  const toggleSort = useCallback((column: UserStatsSortColumn) => {
    setSort((prev) => ({
      column,
      direction: prev.column === column ? (prev.direction === 'DESC' ? 'ASC' : 'DESC') : 'DESC',
    }));
    setOffset(0);
  }, []);

  const setPage = useCallback(
    (page: number) => {
      setOffset(page * limit);
    },
    [limit]
  );

  const setLimit = useCallback((newLimit: number) => {
    setLimitState(newLimit);
    setOffset(0);
  }, []);

  const page = limit > 0 ? Math.floor(offset / limit) : 0;

  return {
    items,
    count,
    loading,
    error,
    query,
    setQuery,
    sortColumn,
    sortDirection,
    toggleSort,
    page,
    limit,
    setPage,
    setLimit,
  };
}
